import { CenterFlexColumn, FlexRow } from "styles/layout";
import { PickName, TrackName } from "../styles";
import {
  selectRosterDisplayName,
  selectTournamentDriverName,
} from "selectors/Roster";
import { MatchTeams } from "model/Match/MatchTeams";
import { CupModeRound } from "model/Match/Set/CupMode/Track/CupModeRound";
import { RoundsRound } from "model/Match/Set/Rounds/Track/RoundsRound";
import { NonTAMatchTrack } from "model/Match/Track/MatchTrack";
import { statTimesBestWithDriverIds, statTimesMedian } from "stats/TimeStats";
import { convertMsToTimeString } from "TimeHelper";
import { getRosterIdFromDriverId } from "model/Roster/Roster";
import { RosterOrgLogo } from "components/Logo/RosterOrgLogo";
import { selectTrackNameFromTournamentId } from "selectors/Tournament";
import { useAppSelector } from "app/hooks";

interface Props {
  trackId: string;
  matchTracks: NonTAMatchTrack[] | undefined;
  teams: MatchTeams;
  pickingRosterId: string | undefined;
  tournamentId: string;
  showPickName?: boolean;
}

export const RoundsBasedCompactTrackStats = ({
  trackId,
  matchTracks,
  teams,
  pickingRosterId,
  tournamentId,
  showPickName = true,
}: Props) => {
  const trackName = useAppSelector((state) =>
    selectTrackNameFromTournamentId(state, trackId, tournamentId)
  );

  const pickName = useAppSelector((state) => {
    if (!pickingRosterId) {
      return "Random";
    }

    return `${selectRosterDisplayName(
      state,
      pickingRosterId,
      tournamentId
    )} Pick`;
  });

  const rounds = (matchTracks ?? []).flatMap(
    (t) => t.rounds as (CupModeRound | RoundsRound)[]
  );

  const times = rounds.flatMap((r) => r.times);
  const { time: best, driverIds: bestDriverIds } =
    statTimesBestWithDriverIds(times);
  const bestString = !!best ? convertMsToTimeString(best) : "-";
  const bestDriverId = bestDriverIds.at(0);
  const bestDisplayName = useAppSelector((state) =>
    !!bestDriverId
      ? selectTournamentDriverName(state, bestDriverId, tournamentId)
      : undefined
  );
  const bestDriverRosterId = !!bestDriverId
    ? getRosterIdFromDriverId(bestDriverId, teams.rosters, teams.scope)
    : undefined;

  const median = statTimesMedian(times);
  const medianString = !!median ? convertMsToTimeString(median) : "-";

  const showTrackStats = rounds.length > 0;
  return (
    <CenterFlexColumn style={{ paddingBottom: "20px" }}>
      <TrackName played={showTrackStats}>
        {trackName}
        {showPickName && <PickName> - {pickName}</PickName>}
      </TrackName>

      {showTrackStats && (
        <CenterFlexColumn
          style={{
            fontSize: "14pt",
            flexWrap: "wrap",
            rowGap: "5px",
          }}
        >
          <FlexRow>
            Best {bestString}{" "}
            <div
              style={{
                padding: "0 4px 0 10px",
                width: "30px",
                height: "32px",
              }}
            >
              {!!bestDriverRosterId && (
                <RosterOrgLogo
                  rosterId={bestDriverRosterId}
                  tournamentId={tournamentId}
                />
              )}
            </div>{" "}
            {bestDisplayName}
          </FlexRow>
          <div>Median {medianString}</div>
        </CenterFlexColumn>
      )}
    </CenterFlexColumn>
  );
};
